import { useEffect, useState } from 'react';
import { Button, Groupper, Header, Icon, Input, Pagination, Table } from 'react-frontier';
import { Dropdown } from 'semantic-ui-react';
import { useParams, Link } from 'react-router-dom';
import { InventoryItem, InventoryType } from '../SiteClasses';
import { bindClick } from '../SiteUtil';
import { useUser } from '../hooks';
import { CreateInventoryItemModal, TransferItemsModal } from '../components';
import UserAccess from '../UserAccess';
import moment from 'moment';
import API from '../API';

const PAGE_SIZE = 20;

interface ModalProps{
	warehouse_id: number,
	type_id: number,
}

var InventoryItemsByType = ({ warehouse_id, type_id }: ModalProps)=>{
	var { hasAccess } = useUser();
	var { warehouseId } = useParams();
	var [items, setItems] = useState<InventoryItem[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [loading, setLoading] = useState<boolean>(false);
	var [filter, setFilter] = useState<string>('');
	var [searchTimeout, setSearchTimeout] = useState<any>(null);
	var [page, setPage] = useState<number>(0);
	var [showItemModal, setShowItemModal] = useState<boolean>(false);
	var [showTransferModal, setShowTransferModal] = useState<boolean>(false);

	useEffect(()=>{
		get();
	}, [warehouse_id, type_id]);

	var get = (get_page?: number, query?: string)=>{
		var p = get_page ?? page;
		var offset = p * PAGE_SIZE;

		setLoading(true);
		API.getInventoryItemsByType(warehouse_id, type_id, offset, PAGE_SIZE, query || null).then(res=>{
			if(res.error) return setLoadError(res.message || 'No se pudieron cargar los equipos.');
			setItems(res.data);
		}).catch(()=>setLoadError('Hubo un error cargando los equipos.')).finally(()=>{
			setLoading(false);
		});
	};

	var onSearchChange = (val: string)=>{
		setFilter(val);
		if(searchTimeout) clearTimeout(searchTimeout);
		if(val.length > 0 && val.length < 2) return;
		setSearchTimeout(setTimeout(()=>{
			setPage(0);
			get(0, val.length >= 2 ? val : null);
		}, 500));
	};

	var onPageChange = (p: number)=>{
		setLoading(true);
		setPage(p);
		get(p, filter.length >= 2 ? filter : null);
	};

	var onItemCreated = (item: InventoryItem)=>{
		setItems(prev=>([
			{ ...item, type_name: items?.[0]?.type_name, last_moved: moment().unix() },
			...(prev || []),
		]));
		setShowItemModal(false);
	};

	var onItemsTransferred = (item_ids: number[])=>{
		setItems(prev=> (prev || []).filter(i=> !item_ids.includes(i.item_id)));
		setShowTransferModal(false);
	};

	var canCreateItems = hasAccess(UserAccess.INVENTORY.CREATE_INVENTORY);
	var canMoveItems = hasAccess(UserAccess.INVENTORY.MOVE_INVENTORY);
	var defaultType: InventoryType = items?.length ? { type_id, type_name: items[0].type_name, type_icon: items[0].type_icon, consumable: items[0].consumable, deleted: 0, user_creator: 0, date_created: 0 } : null;

	if(loadError) return <Header text='Error' subtext={loadError}/>;
	if(!items) return <Header text='Cargando inventario' loading/>;

	return <Groupper
		fitted
		width={800}
		style={{ marginTop: 15}}
		title='Inventario'
		titleRight={
			(canCreateItems || canMoveItems) && (
				<Dropdown icon={null} pointing direction='left' trigger={(
					<Button text='Opciones' color='black' iconRight='caret-down' iconRightStyle={{ marginRight: 0, marginLeft: 5 }} size='small'/>
				)}>
					<Dropdown.Menu>
						{canCreateItems && <Dropdown.Item icon='plus circle' text='Agregar' onClick={bindClick(setShowItemModal, true)}/>}
						{canMoveItems && <Dropdown.Item icon='shuffle' text='Mover' onClick={bindClick(setShowTransferModal, true)}/>}
					</Dropdown.Menu>
				</Dropdown>
			)
		}
	>
		<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, paddingLeft: 10, paddingRight: 10, paddingTop: 10, paddingBottom: 0 }}>
			<Input style={{ flex: 1 }} placeholder='Buscar por ID, nombre, serie o modelo...' value={filter} onChange={onSearchChange}/>
		</div>
		<Table
			striped
			fitted
			selectable
			className='last top'
			headers={['id', 'Tipo', 'Nombre', 'Creado', 'Modelo', 'S/N']}
			collapsingIndexes={[0, 1]}
			footer={(items.length >= PAGE_SIZE || page > 0) && (
				<Table.Cell colSpan={6} row>
					<div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
						<Pagination page={page} onPageChange={onPageChange} hasNext={items.length >= PAGE_SIZE} />
					</div>
				</Table.Cell>
			)}
		>
			{loading ? (
				<Table.Cell row colSpan={6} className='empty'>
					<Header loading text='Cargando' />
				</Table.Cell>
			) : items.length ? items.map(item=>(
				<Table.Row
					compact
					as={Link}
					to={`/warehouses/${warehouseId}/inventory/type/${type_id}/item/${item.item_id}`}
					key={item.item_id}
					data={[
						item.item_id,
						item.type_icon ? <Icon name={item.type_icon}/> : <Icon name='minus'/>,
						item.item_name,
						moment.unix(item.last_moved).format('DD/MM/YY HH:mm'),
						item.model || <Icon name='minus'/>,
						item.serial_number || <Icon name='minus'/>,
					]}
				/>
			)) : (
				<Table.Cell row colSpan={6} className='empty'>No hay equipos registrados con este tipo.</Table.Cell>
			)}
		</Table>
		<CreateInventoryItemModal
			open={showItemModal}
			onClose={bindClick(setShowItemModal, false)}
			warehouse_id={warehouse_id}
			onCreate={onItemCreated}
			defaultType={defaultType}
		/>
		<TransferItemsModal
			open={showTransferModal}
			onClose={bindClick(setShowTransferModal, false)}
			warehouse_id={warehouse_id}
			onTransferred={onItemsTransferred}
			type_id={type_id}
		/>
	</Groupper>;
};

export default InventoryItemsByType;
